import clsx from "clsx";
import { TFunction } from "i18next";
import NorthEastDown from "@/assets/icons/northEastDown.svg?react";
import NorthEastUp from "@/assets/icons/northEastUp.svg?react";
import Progress from "@/components/atomic/Progress";
import { useCategorySummaryStore } from "@/components/ui/category-summary/hooks/useCategorySummaryStore";
import { cn } from "@/lib/utils";
import { EMPTY_PROBLEM_TITLE, Problem } from "@/models/summary";

// 条件判定を行うヘルパー関数
const shouldEnableInteraction = (isComparisionMode: boolean, caseCount: number): boolean => {
  return isComparisionMode && caseCount > 0;
};

interface ProblemCardHeaderProps {
  problem: Problem;
  totalCaseCount: number;
  progressPercentageFloor: number;
  isComparisionMode: boolean;
  isClosed: boolean;
  setIsClosed: (value: boolean) => void;
  t: TFunction;
}

export const ProblemCardHeader: React.FC<ProblemCardHeaderProps> = ({
  problem,
  totalCaseCount,
  progressPercentageFloor,
  isComparisionMode,
  isClosed,
  setIsClosed,
  t,
}) => {
  const { mainSelectedCategoryPath, compSelectedCategoryPath } = useCategorySummaryStore();
  const isSelectingSameCategory = mainSelectedCategoryPath === compSelectedCategoryPath;

  const isIncreased = isComparisionMode && isSelectingSameCategory && problem.fluctuation === "increased";
  const isDecreased = isComparisionMode && isSelectingSameCategory && problem.fluctuation === "decreased";

  // 条件判定と各種スタイルクラスの計算
  const isInteractiveElement = shouldEnableInteraction(isComparisionMode, problem.caseCount);

  // レイアウト関連のクラス
  const layoutClass = isComparisionMode
    ? "w-full flex flex-col justify-start items-start"
    : "w-full flex justify-between items-center";
  const interactiveClass = isInteractiveElement
    ? "cursor-pointer hover:bg-gray-50 rounded-t-[10px] transition-colors"
    : "";

  // テキスト関連のクラス
  const fontStyle = isComparisionMode
    ? "font-bold leading-[1.8]"
    : "font-bold text-xl leading-[36px]";

  // その他のスタイルクラス
  const marginTopClass = isComparisionMode ? "mt-2" : "";
  const paddingLeftClass = isComparisionMode && (!isClosed) ? "pl-7" : "";
  const percentageFontClass = isComparisionMode ? "text-xs" : "text-base";
  const unitFontClass = isComparisionMode ? "text-[8px]" : "text-xs";
  const progressWidthClass = isComparisionMode ? "w-[334px]" : "w-[480px]";

  return (
    <button
      type="button"
      className={clsx(layoutClass, interactiveClass, "border-0 bg-transparent text-left p-0")}
      onClick={() => {
        if (isInteractiveElement) {
          setIsClosed(!isClosed);
        }
      }}
      disabled={!isInteractiveElement}
      aria-expanded={isInteractiveElement ? !isClosed : undefined}
    >
      <h2
        className={cn(
          fontStyle,
          isIncreased && "text-(--compchart-positive)",
          isDecreased && "text-(--compchart-negative)",
        )}
      >
        {problem.title}
        {isIncreased && <NorthEastUp className="inline-block ml-1" />}
        {isDecreased && <NorthEastDown className="inline-block ml-1" />}
      </h2>

      <div className={`flex items-center gap-4 pr-5 ${marginTopClass} ${paddingLeftClass}`}>
        {problem.isEmptyProblem
          ? (
            <>
              <div className="text-[#5F6368]">{EMPTY_PROBLEM_TITLE}</div>
              <div className="flex items-center">
                <div className={`font-bold text-black leading-[1.8] ${percentageFontClass}`}>
                  {problem.caseCount} / {totalCaseCount}
                </div>
                <div className={`font-bold text-black leading-[1.8] ml-1 ${unitFontClass}`}>
                  {t("analysis.problem.cases")}
                </div>
              </div>
            </>
          )
          : (
            <>
              <div className="flex items-center">
                <div className={`font-bold text-black leading-[1.8] ${percentageFontClass}`}>
                  {progressPercentageFloor}
                </div>
                <div className={`font-bold text-black leading-[1.8] ml-1 ${unitFontClass}`}>
                  {t("units.percentage")}
                </div>
              </div>

              <div className={progressWidthClass}>
                <Progress
                  value={progressPercentageFloor}
                  className="h-[15px] rounded-[10px] shadow-[inset_0px_4px_9px_#00000026]"
                />
              </div>

              <div className="flex items-center">
                <div className={`font-bold text-black leading-[1.8] ${percentageFontClass}`}>
                  {problem.caseCount} / {totalCaseCount}
                </div>
                <div className={`font-bold text-black leading-[1.8] ml-1 ${unitFontClass}`}>
                  {t("analysis.problem.cases")}
                </div>
              </div>
            </>
          )}
      </div>
    </button>
  );
};
